import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { createRecording } from "@/lib/playalongEngine";

const MAX_SECS = 180;

export const RecordTake = ({ playing, onStartBand, instrumentId, courseId, lessonLabel, trackTitle }) => {
  const [state, setState] = useState("idle"); // idle | recording | review
  const [secs, setSecs] = useState(0);
  const [blob, setBlob] = useState(null);
  const [withBand, setWithBand] = useState(true);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const rec = useRef(null);
  const url = blob ? URL.createObjectURL(blob) : null;

  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  useEffect(() => {
    if (state !== "recording") return;
    const t = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_SECS) stop(); return s + 1; }), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const start = async () => {
    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false } });
      const mix = await createRecording(mic, withBand);
      const mr = new MediaRecorder(mix.stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => { setBlob(new Blob(chunks, { type: mr.mimeType || "audio/webm" })); mic.getTracks().forEach((t) => t.stop()); mix.cleanup(); setState("review"); };
      rec.current = mr;
      mr.start();
      setSecs(0); setState("recording");
      if (!playing) onStartBand();
    } catch (e) {
      toast.error("Microphone not available — please allow microphone access.");
    }
  };
  const stop = () => rec.current?.state === "recording" && rec.current.stop();
  const discard = () => { setBlob(null); setState("idle"); setNote(""); };

  const send = async () => {
    const fd = new FormData();
    fd.append("instrument", instrumentId); fd.append("course_id", courseId);
    fd.append("message", `Play-along take — ${lessonLabel}, "${trackTitle}".${note.trim() ? `\n\n${note.trim()}` : ""}`);
    fd.append("file", new File([blob], "play-along-take.webm", { type: blob.type || "audio/webm" }));
    setSending(true);
    try {
      await api.post("/tutor-questions", fd);
      toast.success("Sent to Jason! You'll get feedback within 24 hours.");
      discard();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send, please try again");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border bg-background p-5" data-testid="record-take">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Mic size={14} /> Record yourself</p>
      <p className="mt-1 text-xs text-muted-foreground">Wear headphones for the cleanest take. Max {MAX_SECS / 60} minutes.</p>
      {state === "idle" && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button onClick={start} data-testid="record-start" className="flex items-center gap-2 rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-105">
            <Mic size={15} /> Record a take
          </button>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={withBand} onChange={(e) => setWithBand(e.target.checked)} data-testid="record-with-band" /> Include the backing track
          </label>
        </div>
      )}
      {state === "recording" && (
        <div className="mt-3 flex items-center gap-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-red-500" data-testid="record-timer"><span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" /> Recording {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</span>
          <button onClick={stop} data-testid="record-stop" className="flex items-center gap-2 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background"><Square size={13} /> Stop</button>
        </div>
      )}
      {state === "review" && blob && (
        <div className="mt-3 space-y-3">
          <audio controls src={url} className="w-full" data-testid="record-playback" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} data-testid="record-note"
            placeholder="Anything you'd like Jason to listen for? (optional)" className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:border-primary focus:outline-none" />
          <div className="flex flex-wrap gap-2">
            <button onClick={send} disabled={sending} data-testid="record-send" className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send to Jason
            </button>
            <button onClick={discard} data-testid="record-discard" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground"><Trash2 size={14} /> Discard</button>
          </div>
        </div>
      )}
    </div>
  );
};
